import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";

import { numberLocale, t, tc, tn } from "@/localization";

// Pure helpers for Download everything (no React, no network): the list,
// search, paging and each recording's download state.

// Rows shown at first and added by "Show more".
export const PAGE_SIZE = 25;

// A recording as the screen uses it. evt.live sends (GET /api/library):
//   slug, title, room_label, date (ISO, UTC), timezone, location,
//   duration_s, parts, size_bytes, storage ("online" | "archived"),
//   restore_url (archived only: the paid restore, evt.live /api/payRestore),
//   guestbook_messages, download ("ready" | "preparing" | null)
// Only evt.live's paid restore (the events list's Restore) is opened.
const restoreUrlOf = (url) =>
  typeof url === "string" &&
  /^https:\/\/[a-z0-9.-]+\/api\/payRestore\/[A-Za-z0-9-]+$/.test(url)
    ? url
    : null;

export function normalizeRecording(raw = {}) {
  const num = (v) =>
    typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : null;
  return {
    slug: String(raw.slug || ""),
    title: String(raw.title || "").trim() || t("Untitled event"),
    roomLabel: raw.room_label ? String(raw.room_label) : null,
    date: raw.date || null,
    timezone: raw.timezone || null,
    location: raw.location ? String(raw.location) : null,
    durationS: num(raw.duration_s),
    parts: num(raw.parts),
    sizeBytes: num(raw.size_bytes),
    archived: raw.storage === "archived",
    restoreUrl: restoreUrlOf(raw.restore_url),
    guestbookMessages: num(raw.guestbook_messages) || 0,
    download:
      raw.download === "ready" || raw.download === "preparing"
        ? raw.download
        : null,
  };
}

const time = (r) => {
  const ms = r.date ? Date.parse(r.date) : NaN;
  return Number.isFinite(ms) ? ms : 0;
};

// Newest first; recordings without a date go last.
export const sortRecordings = (list) =>
  [...list].sort((a, b) => time(b) - time(a) || a.title.localeCompare(b.title));

export function formatRecordingDate(r) {
  if (!r.date) return null;
  const m = r.timezone ? moment(r.date).tz(r.timezone) : moment(r.date);
  return m.isValid() ? m.format(t("MMM D, YYYY")) : null;
}

// "1 h 12 min", "45 min", "Under a minute".
export function formatDuration(seconds) {
  if (seconds == null) return null;
  const minutes = Math.round(seconds / 60);
  if (minutes < 1) return t("Under a minute");
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return t("{minutes} min", { minutes: m });
  return m
    ? t("{hours} h {minutes} min", { hours: h, minutes: m })
    : t("{hours} h", { hours: h });
}

// "2.1 GB", "850 MB". Decimal units, as phones and computers show file sizes.
export function formatBytes(bytes) {
  if (bytes == null) return null;
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(bytes >= 1e10 ? 0 : 1)} GB`;
  if (bytes >= 1e6) return `${Math.max(1, Math.round(bytes / 1e6))} MB`;
  return t("Under 1 MB");
}

// "Oct 4, 2026 · 1 h 12 min · 3 parts"
export function recordingMeta(r) {
  return [
    formatRecordingDate(r),
    formatDuration(r.durationS),
    r.parts > 1 ? tn("{count} part", "{count} parts", r.parts) : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

// The title as listed: rooms carry their label ("Graduation · Hall B").
export const recordingTitle = (r) =>
  r.roomLabel && !r.title.toLowerCase().includes(r.roomLabel.toLowerCase())
    ? `${r.title} · ${r.roomLabel}`
    : r.title;

// Search by title, room, place or date ("smith", "chapel", "march 2025").
export function searchRecordings(list, query) {
  const words = String(query || "")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return list;
  return list.filter((r) => {
    const m = r.date
      ? r.timezone
        ? moment(r.date).tz(r.timezone)
        : moment(r.date)
      : null;
    const haystack = [
      r.title,
      r.roomLabel,
      r.location,
      m && m.isValid() ? m.format("MMMM MMM D YYYY") : "",
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return words.every((w) => haystack.includes(w));
  });
}

export const countLabel = (n) =>
  tn("{count} recording", "{count} recordings", n, {
    count: n.toLocaleString(numberLocale()),
  });

// ---- Each recording's download -------------------------------------------
//
//   idle       nothing asked yet: Download
//   checking   asking evt.live
//   preparing  the MP4 is being built (or rebuilt): Preparing… (polls)
//   ready      the link works: Download (opens the file)
//   archived   in long-term storage: Request download
//   failed     something went wrong: Try again

export const IDLE = { status: "idle" };

export function fromServer(answer) {
  switch (answer?.status) {
    case "ready":
      return answer.url
        ? {
            status: "ready",
            url: answer.url,
            filename: answer.filename || null,
            sizeBytes:
              typeof answer.size_bytes === "number" ? answer.size_bytes : null,
          }
        : { status: "failed", error: null };
    case "preparing":
      return {
        status: "preparing",
        retryAfterS:
          typeof answer.retry_after_s === "number"
            ? answer.retry_after_s
            : null,
      };
    case "archived":
      return {
        status: "archived",
        restoreUrl: restoreUrlOf(answer.restore_url),
      };
    default:
      return { status: "failed", error: answer?.error || null };
  }
}

// How long to wait before asking again while an MP4 is prepared: what evt.live
// suggests (5–60 s), else 10 s growing to 30 s.
export function pollDelayMs(attempt, retryAfterS) {
  if (typeof retryAfterS === "number" && retryAfterS > 0)
    return Math.min(60, Math.max(5, retryAfterS)) * 1000;
  return Math.min(30, 10 + attempt * 5) * 1000;
}

// A ready link is asked for again after this long rather than reused.
export const LINK_MAX_AGE_MS = 10 * 60 * 1000;

// Builds run a couple at a time for everyone, so one account prepares at most
// this many recordings at once (the rest wait for a free slot).
export const MAX_PREPARING = 3;

// After this long "Preparing…" says it's taking longer than usual; after
// GIVE_UP_MS the row stops checking and offers Try again.
export const SLOW_AFTER_MS = 20 * 60 * 1000;
export const GIVE_UP_MS = 3 * 60 * 60 * 1000;

// Recordings being prepared are remembered on the device ({ slug: startedAt }),
// so coming back to the screen keeps checking instead of starting over.
export function pendingPreparations(saved, now = Date.now()) {
  const out = {};
  if (!saved || typeof saved !== "object" || Array.isArray(saved)) return out;
  for (const [slug, startedAt] of Object.entries(saved)) {
    if (
      typeof startedAt === "number" &&
      startedAt <= now &&
      now - startedAt < GIVE_UP_MS
    )
      out[slug] = startedAt;
  }
  return out;
}

export const preparingCount = (downloads) =>
  Object.values(downloads || {}).filter(
    (d) => d?.status === "preparing" || d?.status === "checking"
  ).length;

// ---- Data files ------------------------------------------------------------

// `file` is what evt.live is asked for; title and description are shown.
export const DATA_FILES = [
  {
    file: "events",
    title: tc("data file", "Event details"),
    description: t("Every event's title, date, place, link and settings"),
    filename: "eventlive-events.csv",
  },
  {
    file: "guests",
    title: t("Guests and registrations"),
    description: t(
      "Names, emails, how each guest joined and what they watched"
    ),
    filename: "eventlive-guests.csv",
  },
  {
    file: "guestbook",
    title: t("Guestbook messages"),
    description: t("Every message with its event, name and time"),
    filename: "eventlive-guestbook.csv",
  },
  {
    file: "viewers",
    title: t("Viewer counts"),
    description: t("Live and replay viewers for each recording"),
    filename: "eventlive-viewers.csv",
  },
  {
    file: "sales",
    title: t("Sales and donations"),
    description: t(
      "Tickets, paid downloads and donations, with amounts and dates"
    ),
    filename: "eventlive-sales.csv",
  },
];
